<template>
  <div>
    <calendar
      :loading="status.isCalendarDataLoading"
      :scheduled-rides="calendarScheduledRides"
      @choose-scheduled-ride="chooseScheduledRide"
      @create-scheduled-ride="createScheduledRide"
      @date-range-changed="onCalendarDateRangeChanged"
    />

    <div
      :id="pageId"
      class="page-content"
      :class="pageId"
    >
      <div class="d-flex justify-content-between">
        <p-button
          v-if="!isBasicPartner"
          type="purple"
          round
          :disabled="isFamilyDashboard && !customerInfo?.id"
          @click="gotoCreateSchedulePage()"
        >
          SCHEDULE A RIDE
        </p-button>

        <p-button
          v-if="isOperatorDashboard"
          type="purple"
          round
          @click="loadScheduledRidesForSpecialRequestAndPharmacy"
        >
          Get All Scheduled Special-Request and Pharmacy Rides
        </p-button>
      </div>

      <hr class="mt-20 mb-20">

      <form
        v-if="!isFamilyDashboard"
        method="post"
        class="form-horizontal"
        autocomplete="off"
        @submit.prevent="loadScheduledRidesByPhoneNumber(true)"
      >
        <fieldset>
          <div
            class="row"
            :class="{
              'align-items-center': !isPartnerDashboard,
            }"
          >
            <label
              class="col-sm-3 control-label"
              :class="{
                'pt-10': isPartnerDashboard,
              }"
            >
              Phone Number {{ !isPartnerDashboard ? '*' : '' }}
            </label>

            <phone-number-input
              v-model="searchValues.phoneNumber"
              class="col-sm-5"
              :page-id="pageId"
              :required="!isPartnerDashboard"
            >
              <template
                v-if="isPartnerDashboard"
                #help-block
              >
                Please keep empty if you are going to get all scheduled rides of your partners.
              </template>
            </phone-number-input>

            <div
              class="col-sm-4 col-12 form-submit-button-group left-button-group"
              :class="{
                'pt-5': isPartnerDashboard,
              }"
            >
              <p-button
                type="purple"
                native-type="submit"
                round
                :pressed="true"
                :disabled="!isPhoneNumberValid && !isPartnerDashboard"
              >
                Get Scheduled Rides
              </p-button>
            </div>
          </div>
        </fieldset>
      </form>

      <form
        v-if="isOperatorDashboard"
        method="post"
        class="form-horizontal"
        autocomplete="off"
        @submit.prevent="loadScheduledRideById"
      >
        <fieldset>
          <div class="row align-items-center">
            <label class="col-sm-3 control-label"> Scheduled Ride ID* </label>
            <div class="col-sm-5">
              <sanitized-input
                id="scheduledRideId"
                v-model="searchValues.scheduledRideId"
                placeholder="Please input scheduled-ride id"
              />
            </div>

            <div class="col-sm-4 col-12 form-submit-button-group left-button-group">
              <p-button
                type="purple"
                native-type="submit"
                round
                :pressed="true"
                :disabled="!searchValues.scheduledRideId"
              >
                Get Scheduled Ride
              </p-button>
            </div>
          </div>
        </fieldset>
      </form>

      <hr v-if="!isFamilyDashboard">

      <fieldset
        v-if="!isFamilyDashboard"
        class="text-center"
      >
        <el-switch
          v-model="status.viewMode"
          class="mb-20"
          active-color="#13ce66"
          inactive-color="#13ce66"
          inactive-text="TABLE VIEW"
          active-value="card"
          inactive-value="table"
          active-text="CARD VIEW"
        />
      </fieldset>

      <fieldset>
        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Status </label>
          <div class="col-sm-8 filter-button-group">
            <p-button
              type="info"
              round
              :disabled="!status.phoneNumberSearch"
              @click="
                filterOptions.isActive = !filterOptions.isActive;
                loadScheduledRidesByPhoneNumber();
              "
            >
              <span
                v-if="filterOptions.isActive"
                class="btn-label"
              ><i class="fa fa-check" /></span>
              Active
            </p-button>

            <p-button
              type="info"
              round
              :disabled="!status.phoneNumberSearch"
              @click="
                filterOptions.isFinished = !filterOptions.isFinished;
                loadScheduledRidesByPhoneNumber();
              "
            >
              <span
                v-if="filterOptions.isFinished"
                class="btn-label"
              ><i class="fa fa-check" /></span>
              Finished
            </p-button>

            <p-button
              type="info"
              round
              :disabled="!status.phoneNumberSearch"
              @click="
                filterOptions.isCanceled = !filterOptions.isCanceled;
                loadScheduledRidesByPhoneNumber();
              "
            >
              <span
                v-if="filterOptions.isCanceled"
                class="btn-label"
              ><i class="fa fa-check" /></span>
              Canceled
            </p-button>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Type </label>
          <div class="col-sm-8 filter-button-group">
            <p-button
              type="info"
              round
              :disabled="!status.phoneNumberSearch"
              @click="
                filterOptions.isSingle = !filterOptions.isSingle;
                loadScheduledRidesByPhoneNumber();
              "
            >
              <span
                v-if="filterOptions.isSingle"
                class="btn-label"
              ><i class="fa fa-check" /></span>
              Single
            </p-button>

            <p-button
              type="info"
              round
              :disabled="!status.phoneNumberSearch"
              @click="
                filterOptions.isRepeated = !filterOptions.isRepeated;
                loadScheduledRidesByPhoneNumber();
              "
            >
              <span
                v-if="filterOptions.isRepeated"
                class="btn-label"
              ><i class="fa fa-check" /></span>
              Repeated
              <i class="fa fa-repeat" />
            </p-button>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <div class="row align-items-center">
          <pagination
            :current-page="paginationInfo.currentPage"
            :total-pages="paginationInfo.totalPages"
            @load-page-data="getScheduledRides"
          />
        </div>
      </fieldset>

      <message-html :result-html="status.resultHtml" />

      <fieldset v-if="isEmptyScheduledWarningShow">
        <label class="col-sm-1 control-label" />
        <div class="col-sm-10">
          <p>User does not have any scheduled rides.</p>
          <p>Add a scheduled ride that they would like to be ordered for them automatically.</p>
        </div>
      </fieldset>

      <fieldset
        v-if="isCardView"
        class="custom-collapse"
      >
        <h5
          v-if="!isEmptyScheduledWarningShow"
          style="font-size: 18px; font-weight: bold"
        >
          Below is a list of your scheduled rides. Click on one of the rows below to view or edit details.
        </h5>

        <el-collapse v-model="openedCollapseIds">
          <scheduled-ride-item
            v-for="(scheduledRide, index) in scheduledRides"
            :key="index"
            :scheduled-ride="scheduledRide"
            :is-action-allowed="!isBasicPartner"
            :is-collapse-opened="isCollapseOpened(scheduledRide.collapseId!)"
            @reload-calendar-data="loadScheduledRidesForCalendar"
            @duplicate-scheduled-ride="duplicateScheduledRide"
          />
        </el-collapse>
      </fieldset>

      <table-view-container
        v-if="isTableView"
        class="pt-10 pb-10 pl-10 pr-10"
        :page-id="pageId"
        :data="scheduledRides"
        :table-columns="tableColumns"
        :fixed-column-counts="3"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, toRefs, reactive, computed, watch, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { cloneDeep } from 'lodash';
import { ElLoading } from 'element-plus';
import events from '@/events';
import Moment from 'moment-timezone';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { useProfile } from '@/use/profile';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { useRide } from '@/use/ride';
import { scheduledRideService } from '@/services/scheduledRide';
import { ScheduledRide, ScheduledRidesQueryInput, ExtendedScheduledRide } from '@/models/scheduledRide';

const Calendar = defineAsyncComponent(() => import('./ScheduleRide/Calendar.vue') as any);

const ScheduledRideItem = defineAsyncComponent(() => import('./ScheduleRide/ScheduledRideItem.vue') as any);

type ScheduledRideCalendarItem = {
  title: string;
  startStr: string;
  start: string;
  end: string;
  allDay: boolean;
  scheduledRideId: number;
  className: string;
};

const formatRecurringIcon = (value: boolean) => {
  if (value) {
    return '<i class="fa fa-repeat" style="font-size:20px;" />';
  }

  return '';
};

const pageId = 'scheduled-rides-list';
const paginationLimit = 10;
const tableColumns = [
  { label: 'Recurring', field: 'rideMultiple', width: 80, isCenterAlign: true, isDisableSort: true, formatter: formatRecurringIcon },
  { label: 'Scheduled Ride ID', field: 'id', width: 150 },
  { label: 'Caller Id', field: 'callerId', width: 180 },
  { label: 'Executed At', field: 'executedAt', width: 180, isDateField: true },
  { label: 'Call To Confirm Surge', field: 'callToConfirmSurge', width: 160, isCapitalize: true, formatter: util.convertBooleanToYesNo },
  { label: 'Completed At', field: 'completedAt', width: 180, isDateField: true },
  { label: 'Canceled At', field: 'canceledAt', width: 180, isDateField: true },
  { label: 'Updated At', field: 'updatedAt', width: 180, isDateField: true },
  { label: 'Last Ride At', field: 'lastRideAt', width: 180, isDateField: true },
  { label: 'Emailed Reminder At', field: 'emailedReminderAt', width: 180, isDateField: true },
  { label: 'Called Reminder At', field: 'calledReminderAt', width: 180, isDateField: true },
  { label: 'First Pickup', field: 'firstPickup', width: 180, isDateField: true },
  { label: 'Rider Phone', field: 'riderPhone', width: 120 },
  { label: 'Ride Name', field: 'rideName', width: 160 },
  { label: 'Ride Type', field: 'rideType', width: 100, isCapitalize: true },
  { label: 'Ride Multiple', field: 'rideMultiple', width: 120, formatter: util.convertBooleanToYesNo },
  { label: 'Partner Id', field: 'partnerId', width: 100 },
  { label: 'Pickup Date', field: 'pickupDate', width: 120 },
  { label: 'Start Date', field: 'startDate', width: 120 },
  { label: 'Time Zone', field: 'timeZone', width: 160 },
  { label: 'End After X Times', field: 'endAfterXTimes', width: 180 },
  { label: 'End On', field: 'endOn', width: 120 },
  { label: 'Sun', field: 'day0', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Mon', field: 'day1', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Tue', field: 'day2', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Wed', field: 'day3', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Thu', field: 'day4', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Fri', field: 'day5', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Sat', field: 'day6', width: 80, formatter: util.convertBooleanToYesOrBlank },
  { label: 'Repeat Every X Week', field: 'repeatEveryXWeekText', width: 180 },
  { label: 'Street Address 1', field: 'streetAddress1', width: 180 },
  { label: 'City 1', field: 'city1', width: 120 },
  { label: 'State 1', field: 'state1', width: 80 },
  { label: 'Zip 1', field: 'zip1', width: 80 },
  { label: 'Lat 1', field: 'lat1', width: 120 },
  { label: 'Long 1', field: 'long1', width: 120 },
  { label: 'Street Address 2', field: 'streetAddress2', width: 180 },
  { label: 'City 2', field: 'city2', width: 120 },
  { label: 'State 2', field: 'state2', width: 80 },
  { label: 'Zip 2', field: 'zip2', width: 80 },
  { label: 'Lat 2', field: 'lat2', width: 120 },
  { label: 'Long 2', field: 'long2', width: 120 },
  { label: 'Note To Driver', field: 'noteToDriver', width: 280 },
  { label: 'Cancellation Reason', field: 'cancellationReason', width: 180 },
  { label: 'Call Attempts', field: 'callAttempts', width: 140 },
  { label: 'Customer Cost Override', field: 'customerCostOverride', width: 180 },
  { label: 'Created At', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Next Pickup Time String', field: 'nextPickupTimeString', width: 180 },
  { label: 'Created At Website', field: 'createdAtWebsite', width: 260, isReadMore: true, maxLength: 40 },
  { label: 'Custom Rider Name', field: 'customRiderName', width: 180 },
  { label: 'Skip Dates', field: 'skipDates', width: 120 },
  { label: 'Expand Driver Search', field: 'isExpandDriverSearch', width: 170, formatter: util.convertBooleanToYesNo },
  { label: 'Expand Vehicle Search', field: 'isExpandVehicleSearch', width: 170, formatter: util.convertBooleanToYesNo },
];

const route = useRoute();
const router = useRouter();
const { customerInfo, customerPhoneNumber } = useCustomer();

const { dashboardCategory, isOperatorDashboard, isFamilyDashboard, isPartnerDashboard } = useDashboard();

const { userRoles } = useProfile();
const { isPremiumPartner } = toRefs(userRoles);

const { showConfirmPopupDialog } = useSwalDialog();
const { partnerInfo, partnerCode } = usePartner();
const { scheduledRideId, setScheduledRideId, setDuplicatedScheduledRide } = useRide();

// original scheduled rides from API
const backupScheduledRides: Ref<ScheduledRide[]> = ref([]);
// formatted scheduled rides
const scheduledRides: Ref<ExtendedScheduledRide[]> = ref([]);
const calendarScheduledRides: Ref<ScheduledRideCalendarItem[]> = ref([]);

const backupPhoneNumber: Ref<string | null> = ref(null);
const openedCollapseIds: Ref<string[]> = ref([]);

const searchValues = reactive({
  phoneNumber: '',
  scheduledRideId: '',
});

const calendarDateRange = reactive({
  startDate: '',
  endDate: '',
});

const filterOptions = reactive({
  isActive: true,
  isFinished: true,
  isCanceled: true,
  isSingle: true,
  isRepeated: true,
});

const status = reactive({
  isCalendarDataLoading: false,
  resultHtml: '',
  viewMode: 'card',
  phoneNumberSearch: true,
});

const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});

const searchQuery: Ref<ScheduledRidesQueryInput> = ref({});

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(searchValues.phoneNumber));
const isEmptyScheduledWarningShow = computed(() => customerPhoneNumber.value && scheduledRides.value.length === 0);
const isCardView = computed(() => status.viewMode === 'card');
const isTableView = computed(() => status.viewMode === 'table');
const isBasicPartner = computed(() => isPartnerDashboard.value && partnerInfo.value?.plan === 'basic');

const duplicateScheduledRide = (scheduledRide: ExtendedScheduledRide) => {
  const duplicatedScheduledRide = backupScheduledRides.value.find((item: ScheduledRide) => item.id === scheduledRide.id);

  if (duplicatedScheduledRide) {
    setDuplicatedScheduledRide(duplicatedScheduledRide);
    gotoCreateSchedulePage();
  }
};

const isCollapseOpened = (collapseId: string) => {
  return openedCollapseIds.value.includes(collapseId);
};

const getScheduledRides = async (currentPage: number = 1) => {
  paginationInfo.currentPage = currentPage;
  searchValues.phoneNumber = util.getValidPhoneNumber(searchValues.phoneNumber);
  status.resultHtml = '';
  scheduledRides.value = [];

  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Scheduled Rides...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const query = {
      ...searchQuery.value,
      ...(isPartnerDashboard.value && { partnerCode: partnerCode.value }),
      pagination: {
        limit: paginationLimit,
        offset: (currentPage - 1) * paginationLimit,
      },
    };

    const response = isPartnerDashboard.value
      ? await scheduledRideService.fetchPartnerScheduledRideRecords(query)
      : await scheduledRideService.fetchScheduledRideRecords(query);

    paginationInfo.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    backupScheduledRides.value = cloneDeep(response.rows);
    scheduledRides.value = response.rows.map((scheduledRide: ScheduledRide) => rideUtility.formatScheduledRideData(scheduledRide));

    util.moveToTop();
  } catch (error) {
    paginationInfo.totalPages = 1;
    paginationInfo.currentPage = 1;
    scheduledRides.value = [];
    backupScheduledRides.value = [];
    status.resultHtml = util.getGraphqlError(error);
  }

  loading.close();
};

const chooseScheduledRide = async (selectedScheduledRideId: string) => {
  searchValues.scheduledRideId = selectedScheduledRideId;
  await loadScheduledRideById();
  openedCollapseIds.value = [`collapse-${selectedScheduledRideId}`];
};

const gotoCreateSchedulePage = (date: string = '') => {
  const query: {
    rideFormType: string;
    phoneNumber?: string;
    startDate?: string;
  } = {
    rideFormType: 'schedule',
  };

  if (backupPhoneNumber.value && !isFamilyDashboard.value) {
    query.phoneNumber = backupPhoneNumber.value;
  }

  if (date) {
    query.startDate = date;
  }

  router.push({
    path: `/${dashboardCategory.value}/ride-ordering`,
    query,
  });
};

const createScheduledRide = async (date: string) => {
  if (isPartnerDashboard.value && !isPremiumPartner.value) {
    return;
  }

  const formatedDate = util.formatDateTime(date, 'ddd, MMMM DD, YYYY');
  const isConfirmed = await showConfirmPopupDialog({
    text: `Do you want to create the scheduled ride<br>on ${formatedDate}?`,
    title: 'Create Scheduled Ride?',
    confirmButtonText: 'Create',
    type: 'html',
  });

  if (!isConfirmed) {
    return;
  }

  gotoCreateSchedulePage(date);
};

const loadScheduledRidesByPhoneNumber = async (isReloadCalendar: boolean = false) => {
  backupPhoneNumber.value = util.getValidPhoneNumber(searchValues.phoneNumber);
  status.phoneNumberSearch = true;

  searchQuery.value = {
    phoneNumber: backupPhoneNumber.value,
    isActive: filterOptions.isActive,
    isFinished: filterOptions.isFinished,
    isCanceled: filterOptions.isCanceled,
    isSingle: filterOptions.isSingle,
    isRepeated: filterOptions.isRepeated,
  };

  await getScheduledRides();
  isReloadCalendar && (await loadScheduledRidesForCalendar());
};

const loadScheduledRideById = () => {
  status.phoneNumberSearch = false;
  searchQuery.value = {
    scheduledRideId: Number(searchValues.scheduledRideId),
  };

  getScheduledRides();
};

const loadScheduledRidesForSpecialRequestAndPharmacy = async () => {
  status.phoneNumberSearch = false;
  searchQuery.value = {
    isAllSpecialRequestAndPharmacy: true,
  };

  await getScheduledRides();
  await loadScheduledRidesForCalendar();
};

const getCalendarEventStatus = (scheduledRide: ScheduledRide) => {
  const { completedAt, canceledAt, confirmedAt } = scheduledRide;

  if (completedAt) {
    return 'finished-event';
  } else if (canceledAt) {
    return 'canceled-event';
  } else if (confirmedAt) {
    return 'confirmed-event';
  } else {
    return 'active-event';
  }
};

const getCalendarClassName = (scheduledRide: ScheduledRide) => {
  const statusClassName = getCalendarEventStatus(scheduledRide);
  const roundTripClassName = scheduledRide.returnTime ? 'round-trip' : '';

  return `${statusClassName} ${roundTripClassName} ${scheduledRide.serviceType}`;
};

const loadScheduledRidesForCalendar = async () => {
  if (!searchValues.phoneNumber && !isPartnerDashboard.value && !searchQuery.value.isAllSpecialRequestAndPharmacy) {
    return;
  }

  if (!calendarDateRange.startDate && !calendarDateRange.endDate) {
    return;
  }

  calendarScheduledRides.value = [];
  status.isCalendarDataLoading = true;

  try {
    const data = {
      phoneNumber: searchValues.phoneNumber,
      startDate: calendarDateRange.startDate,
      endDate: calendarDateRange.endDate,
      isAllSpecialRequestAndPharmacy: searchQuery.value.isAllSpecialRequestAndPharmacy,
      ...(isPartnerDashboard.value && { partnerCode: partnerCode.value }),
    };

    const tempEvents: ScheduledRideCalendarItem[] = [];
    const response = isPartnerDashboard.value
      ? await scheduledRideService.fetchPartnerScheduledRideCalendarRecords(data)
      : await scheduledRideService.fetchScheduledRideCalendarRecords(data);

    for (const scheduledRide of response.rows) {
      const dateTimes = scheduledRide.dateTimes ?? [];

      for (const dateTime of dateTimes) {
        const eventStart = util.formatDateTime(dateTime, 'YYYY-MM-DDTHH:mm:ss');
        const eventEnd = util.formatDateTime(Moment(eventStart).add(30, 'minutes'), 'YYYY-MM-DDTHH:mm:ss');
        const endOfDay = util.formatDateTime(Moment(eventStart).endOf('day'), 'YYYY-MM-DDTHH:mm:ss');

        tempEvents.push({
          title: scheduledRide.rideName ?? '',
          startStr: 'start',
          start: eventStart,
          // if event end value is another date of start, need to set end of day to prevent display event in another dates
          /*
            ex: startEvent: 2021-05-10 11:50, endEvent: 2021-05-11 00:20
            if start and end dates are in difference dates, the event will be show duplicated in 2 datesm and it is an issue.
          */
          end: eventEnd > endOfDay ? endOfDay : eventEnd,
          allDay: false,
          scheduledRideId: scheduledRide.id,
          className: getCalendarClassName(scheduledRide),
        });
      }

      calendarScheduledRides.value = cloneDeep(tempEvents);
    }
  } catch (error) {
    console.log(error);
  }

  status.isCalendarDataLoading = false;
};

const onCalendarDateRangeChanged = (startDate: string, endDate: string) => {
  const isFirstRendered = !calendarDateRange.startDate || !calendarDateRange.endDate;
  calendarDateRange.startDate = startDate;
  calendarDateRange.endDate = endDate;

  !isFirstRendered && loadScheduledRidesForCalendar();
};

const resetForm = () => {
  searchValues.phoneNumber = '';
  searchValues.scheduledRideId = '';
  backupPhoneNumber.value = null;
  status.resultHtml = '';
  scheduledRides.value = [];

  filterOptions.isActive = true;
  filterOptions.isFinished = true;
  filterOptions.isCanceled = true;
  filterOptions.isSingle = true;
  filterOptions.isRepeated = true;

  paginationInfo.currentPage = 1;
  paginationInfo.totalPages = 1;
};

watch(customerPhoneNumber, (value: string) => {
  if (value && value === searchValues.phoneNumber) {
    return;
  }

  resetForm();
  searchValues.phoneNumber = value;
  backupPhoneNumber.value = value;

  if (value || isPartnerDashboard.value) {
    loadScheduledRidesByPhoneNumber(true);
  }
});

onMounted(() => {
  util.moveToTop();

  const { query } = route;
  const { scheduledRideId: queryScheduledRideId } = query;

  searchValues.phoneNumber = customerPhoneNumber.value;

  if (scheduledRideId.value) {
    searchValues.scheduledRideId = scheduledRideId.value.toString();
    setScheduledRideId(0);
    loadScheduledRideById();
  } else if (queryScheduledRideId) {
    searchValues.scheduledRideId = queryScheduledRideId as string;
    loadScheduledRideById();
  } else if (searchValues.phoneNumber || isPartnerDashboard.value) {
    loadScheduledRidesByPhoneNumber(true);
  }

  events.on('reset-page', resetForm);
});

onBeforeUnmount(() => {
  events.off('reset-page', resetForm);
});
</script>

<style lang="scss">
.scheduled-rides-list {
  padding-bottom: 20px;
  .custom-collapse {
    @media (max-width: 576px) {
      margin-left: -5px;
      margin-right: -5px;
    }
    .el-collapse-item {
      .change-collapse {
        .el-collapse-item {
          border: none;
          .el-collapse-item__wrap {
            border: 0;
            background: transparent;
          }
          .el-collapse-item__content {
            background: transparent;
          }
          .el-collapse-item__header {
            background: transparent;
            border: 0;
            span {
              padding-right: 20px;
            }
          }
        }
        .el-collapse-item__header {
          padding-left: 0;
          line-height: 25px;
          color: #5e8283;
          font-size: 16px;
          @media (max-width: 576px) {
            font-size: 13px;
            line-height: 20px;
          }
          .el-collapse-item__arrow {
            height: 25px;
            font-weight: 900;
            @media (max-width: 576px) {
              right: -10px;
            }
          }
        }
        ul {
          padding-left: 20px;
          padding-right: 20px;
          margin-bottom: 0;
          li {
            font-size: 14px;
            @media (max-width: 576px) {
              font-size: 12px;
            }
          }
        }
      }
      .left-container {
        table td {
          &:nth-child(odd) {
            min-width: 180px;
            width: 180px;
            @media (max-width: 576px) {
              min-width: 140px;
              width: 140px;
            }
          }
        }
      }
      .google-map-container .el-collapse .el-collapse-item {
        margin-top: 0;
        border: 1px solid #7ac29a;

        .el-collapse-item__arrow {
          height: 40px;
        }
      }
    }
  }
  .form-submit-button-group {
    position: relative;
    @media (max-width: 576px) {
      button {
        margin-bottom: 5px !important;
      }
    }
    .display-ride {
      margin-right: 20px;
      @media (max-width: 576px) {
        margin-right: 0;
      }
    }
  }
}
</style>
